<template>
    <TooltipProvider v-if="showTooltip">
        <Tooltip :disabled="!isOverflowing">
            <TooltipTrigger as-child>
                <span ref="textElement" class="block truncate overflow-hidden text-ellipsis">
                    {{ displayValue }}
                </span>
            </TooltipTrigger>
            <TooltipContent class="w-max max-w-[24rem] text-left whitespace-normal break-words">
                {{ tooltipValue }}
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
    <span v-else class="block truncate overflow-hidden text-ellipsis">{{ displayValue }}</span>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const props = withDefaults(
    defineProps<{
        value?: string | number | null;
        emptyText?: string;
        tooltip?: boolean;
        tooltipMaxLength?: number;
    }>(),
    {
        emptyText: '-',
        tooltip: false,
        tooltipMaxLength: 255,
    },
);

const displayValue = computed(() => {
    if (props.value === null || props.value === undefined) {
        return props.emptyText;
    }
    return String(props.value);
});

const showTooltip = computed(() => props.tooltip && props.value !== null && props.value !== undefined);
const tooltipValue = computed(() => {
    if (displayValue.value.length <= props.tooltipMaxLength) return displayValue.value;
    return `${displayValue.value.slice(0, props.tooltipMaxLength)}…`;
});

const textElement = ref<HTMLElement | null>(null);
const isOverflowing = ref(false);
let resizeObserver: ResizeObserver | undefined;

function updateOverflow() {
    const element = textElement.value;
    isOverflowing.value = element !== null && element.scrollWidth > element.clientWidth;
}

onMounted(async () => {
    await nextTick();
    updateOverflow();

    if (textElement.value && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(updateOverflow);
        resizeObserver.observe(textElement.value);
    }
});

watch(displayValue, async () => {
    await nextTick();
    updateOverflow();
});

onBeforeUnmount(() => resizeObserver?.disconnect());
</script>
